export const BLANK_LINE_MARKER = "__BLANK_LINE__"

/** Minimum blank lines on the longest column */
export const MIN_BLANK_LINE_COUNT = 2

export type ToolSupplyItem = { name: string; out: boolean; in: boolean }

export function isBlankLineName(name: string): boolean {
    return name === BLANK_LINE_MARKER
}

export function stripBlankLineNames(items: string[]): string[] {
    return items.filter((item) => !isBlankLineName(item))
}

export function stripBlankLineItems(items: ToolSupplyItem[]): ToolSupplyItem[] {
    return items.filter((item) => !isBlankLineName(item.name))
}

/** Approximate printable height of US Letter page 1 (11in minus typical margins) */
export function getPrintablePageHeightPx(): number {
    if (typeof window !== "undefined" && window.matchMedia("print").matches) {
        const viewportHeight = window.innerHeight
        if (viewportHeight >= 500 && viewportHeight <= 1200) {
            return viewportHeight
        }
    }

    const CSS_PIXELS_PER_INCH = 96
    const PAGE_HEIGHT_IN = 11
    const PRINT_MARGIN_IN = 0.5
    return (PAGE_HEIGHT_IN - PRINT_MARGIN_IN * 2) * CSS_PIXELS_PER_INCH
}

/**
 * Longest column: at least 2 blank lines; more if they fit on page 1.
 * When content overflows page 1, longest column gets exactly 2 blank lines.
 * Shorter column: enough blank lines to match the longest column's total height.
 */
export function calculateChecklistBlankLineCounts(options: {
    pageHeightPx: number
    toolsItemsBottomPx: number
    suppliesItemsBottomPx: number
    toolsItemCount: number
    suppliesItemCount: number
    rowStepPx: number
}): { tools: number; supplies: number } {
    const {
        pageHeightPx,
        toolsItemsBottomPx,
        suppliesItemsBottomPx,
        toolsItemCount,
        suppliesItemCount,
        rowStepPx,
    } = options

    if (rowStepPx <= 0) {
        return { tools: MIN_BLANK_LINE_COUNT, supplies: MIN_BLANK_LINE_COUNT }
    }

    const toolsIsLonger = toolsItemCount >= suppliesItemCount
    const longerItemCount = Math.max(toolsItemCount, suppliesItemCount)
    const shorterItemCount = Math.min(toolsItemCount, suppliesItemCount)
    const itemRowDifference = longerItemCount - shorterItemCount

    const longerItemsBottomPx = toolsIsLonger
        ? toolsItemsBottomPx
        : suppliesItemsBottomPx

    const overflowsPage1 = longerItemsBottomPx > pageHeightPx

    let longerColumnBlanks: number
    if (overflowsPage1) {
        longerColumnBlanks = MIN_BLANK_LINE_COUNT
    } else {
        const remainingBelow = pageHeightPx - longerItemsBottomPx
        longerColumnBlanks = Math.max(
            MIN_BLANK_LINE_COUNT,
            Math.floor(remainingBelow / rowStepPx)
        )
    }

    const shorterColumnBlanks = longerColumnBlanks + itemRowDifference

    return toolsIsLonger
        ? { tools: longerColumnBlanks, supplies: shorterColumnBlanks }
        : { tools: shorterColumnBlanks, supplies: longerColumnBlanks }
}
